/**
 * THE DISPLAY EDITS, once (phone display build, PR1). The desk panel (display-panel.tsx)
 * and the phone sheet (display-sheet.tsx) both let a person add, remove, reorder and
 * re-mode grouping levels and pick the sort; the markup differs (an anchored popover with
 * mini-menus vs a bottom sheet with pill rows) but the EDITS are one set of facts, and
 * two copies would drift — which "Move up" or "Remove" is legal is exactly the kind of
 * rule that would end up disagreeing. Pure: no React, no DOM.
 */
import { MAX_LEVELS, type DisplayConfig, type EntityDisplaySpec, type GroupAxis, type LevelConfig, type OrderMode, type SortAxis, type SortDir } from './engine.js';

export function modeLabel(mode: OrderMode, dir: SortDir): string {
  if (mode === 'alpha') return dir === 'asc' ? 'A → Z' : 'Z → A';
  if (mode === 'workflow') return 'Workflow order';
  return dir === 'asc' ? 'Lowest first' : 'Highest first';
}

/** Every (mode, dir) pair an axis honestly offers — workflow order has one direction. */
export function orderChoices<Row>(axis: GroupAxis<Row> | undefined): { mode: OrderMode; dir: SortDir }[] {
  return (axis?.orderModes ?? ['alpha']).flatMap((mode) =>
    (mode === 'workflow' ? (['asc'] as const) : (['asc', 'desc'] as const)).map((dir) => ({ mode, dir })),
  );
}

export interface DisplayEdit<Row> {
  freeAxes: GroupAxis<Row>[];
  sortAxis: SortAxis<Row> | undefined;
  canAdd: boolean;
  axisById: (id: string) => GroupAxis<Row> | undefined;
  setAxis: (i: number, id: string) => void;
  setOrder: (i: number, mode: OrderMode, dir: SortDir) => void;
  addLevel: (id: string) => void;
  removeLevel: (i: number) => void;
  moveLevel: (i: number, delta: number) => void;
  setLevels: (levels: LevelConfig[]) => void;
  setSort: (id: string, dir: SortDir) => void;
}

export function displayEdit<Row>(
  spec: EntityDisplaySpec<Row>,
  config: DisplayConfig,
  onChange: (next: DisplayConfig) => void,
  maxLevels: number = MAX_LEVELS,
): DisplayEdit<Row> {
  const usedAxes = new Set(config.levels.map((l) => l.axis));
  const freeAxes = spec.groupAxes.filter((a) => !usedAxes.has(a.id));
  const axisById = (id: string): GroupAxis<Row> | undefined => spec.groupAxes.find((a) => a.id === id);
  const setLevels = (levels: LevelConfig[]): void => onChange({ ...config, levels });
  const patch = (i: number, p: Partial<LevelConfig>): void =>
    setLevels(config.levels.map((l, j) => (j === i ? { ...l, ...p } : l)));
  return {
    freeAxes,
    sortAxis: spec.sortAxes.find((a) => a.id === config.sortAxis),
    canAdd: config.levels.length < maxLevels && freeAxes.length > 0,
    axisById,
    setAxis: (i, id) => {
      const a = axisById(id);
      patch(i, { axis: id, orderMode: a?.orderModes[0] ?? 'alpha', dir: 'asc' });
    },
    setOrder: (i, mode, dir) => patch(i, { orderMode: mode, dir }),
    addLevel: (id) => {
      const a = axisById(id);
      setLevels([...config.levels, { axis: id, orderMode: a?.orderModes[0] ?? 'alpha', dir: 'asc' }]);
    },
    removeLevel: (i) => setLevels(config.levels.filter((_, j) => j !== i)),
    moveLevel: (i, delta) => {
      const j = i + delta;
      if (j < 0 || j >= config.levels.length) return;
      const next = [...config.levels];
      const a = next[i];
      const b = next[j];
      if (a === undefined || b === undefined) return;
      next[i] = b;
      next[j] = a;
      setLevels(next);
    },
    setLevels,
    // Setting a sort from either surface is CHOOSING it (engine.ts `sortChosen`): the chip lights.
    setSort: (id, dir) => onChange({ ...config, sortAxis: id, sortDir: dir, sortChosen: true }),
  };
}
